﻿import Phaser from 'phaser'
import type { MatchEngine } from '@engine/MatchEngine'
import type { Element, PlacedTower } from '@interfaces/index'
import { TEXTURE_KEYS } from '@interfaces/index'
import { cartToIso } from '@engine/Grid'
import type { GridRenderer } from './GridRenderer'
import { AssetRegistry } from '../AssetRegistry'

const ELEMENT_COLORS: Record<Element, number> = {
  fire:    0xE8613A,
  water:   0x3A8AE8,
  nature:  0x4AAA3A,
  elektro: 0xE8D030,
  light:   0xF0E8FF,
  shadow:  0x8A50D0,
  neutral: 0x888888,
}

const ELEMENT_DARK: Record<Element, number> = {
  fire:    0xA03018,
  water:   0x1A5AAA,
  nature:  0x237818,
  elektro: 0xA89010,
  light:   0xC0B8CC,
  shadow:  0x4A2090,
  neutral: 0x555555,
}

// Abbreviation map for tower IDs shown above the diamond (procedural fallback only)
const ABBREV: Record<string, string> = {
  archer:    'B',
  firemage:  'FM',
  frost:     'FR',
  lightning: 'BL',
  shadow:    'SK',
  goldmine:  'GM',
  mudpit:    'SG',
  thorns:    'DF',
  wall:      'M',
}

function abbrev(id: string): string {
  return ABBREV[id] ?? id.slice(0, 2).toUpperCase()
}

interface TowerObject {
  gfx: Phaser.GameObjects.Graphics
  label: Phaser.GameObjects.Text
}

interface TowerRenderState {
  level: number
  path: 'A' | 'B' | null
}

export class TowerRenderer {
  private towerObjects: Map<string, TowerObject> = new Map()
  // Abbreviation / level labels rendered above each tower
  private towerLabels: Map<string, Phaser.GameObjects.Text> = new Map()
  // Sprites for towers with real atlas assets.
  // Phaser.GameObjects.Sprite (not Image) allows setFrame() for per-level animation.
  private towerSprites: Map<string, Phaser.GameObjects.Sprite> = new Map()
  // Track last rendered frame key per instanceId to skip redundant setFrame() calls
  private towerSpriteFrames: Map<string, string> = new Map()
  // Dirty-flag cache: skip redraw when tower state and origin are unchanged.
  // Key = instanceId, value = last-rendered level + path.
  // Cleared on resize (originX/Y change) so all towers redraw on next frame.
  private towerRenderStates: Map<string, TowerRenderState> = new Map()
  private _lastOriginKey = ''
  private readonly scene: Phaser.Scene
  private readonly engine: MatchEngine
  private playerId: string
  private readonly gridRenderer: GridRenderer

  constructor(
    scene: Phaser.Scene,
    engine: MatchEngine,
    playerId: string,
    gridRenderer: GridRenderer
  ) {
    this.scene = scene
    this.engine = engine
    this.playerId = playerId
    this.gridRenderer = gridRenderer

    engine.eventBus.on('TOWER_DEMOLISHED', (e) => {
      const instanceId = e.payload['instanceId'] as string | undefined
      if (instanceId !== undefined) {
        this._destroyTowerObject(instanceId)
      }
    })
  }

  update(): void {
    const grid = this.engine.getGrid(this.playerId)
    if (grid === undefined) return

    const towers = grid.getTowers()
    const { cellW, cellH, originX, originY } = this.gridRenderer

    // Invalidate render-state cache when the grid origin changes (e.g. on resize).
    // All towers must redraw at new pixel positions on the next frame.
    const originKey = `${originX},${originY}`
    if (originKey !== this._lastOriginKey) {
      this.towerRenderStates.clear()
      this._lastOriginKey = originKey
    }

    for (const tower of towers) {
      const { gx, gy } = tower
      const iso = cartToIso(gx, gy, cellW, cellH)
      const sx = iso.x + originX
      const sy = iso.y + originY
      const hh = cellH * 0.44

      // ── Atlas sprite path: use real per-entity atlas when available ────────
      if (AssetRegistry.hasTowerAtlas(tower.id, this.scene)) {
        this._renderTowerSprite(tower, sx, sy, hh, cellH)
        // Hide any pre-existing procedural object for this instance
        const existingObj = this.towerObjects.get(tower.instanceId)
        if (existingObj !== undefined) {
          existingObj.gfx.setVisible(false)
          existingObj.label.setVisible(false)
        }
        const existingLabel = this.towerLabels.get(tower.instanceId)
        if (existingLabel !== undefined) existingLabel.setVisible(false)
        continue
      }

      // ── Procedural fallback ───────────────────────────────────────────────
      const hw = cellW * 0.44

      let obj = this.towerObjects.get(tower.instanceId)
      if (obj === undefined) {
        const gfx = this.scene.add.graphics()
        gfx.setDepth(5)
        const label = this.scene.add
          .text(sx, sy, '', {
            fontSize: '9px',
            color: '#ffffff',
            fontFamily: 'monospace',
            stroke: '#000000',
            strokeThickness: 2,
          })
          .setOrigin(0.5, 0.5)
          .setDepth(6)
        obj = { gfx, label }
        this.towerObjects.set(tower.instanceId, obj)
      }

      // Create or reuse abbreviation label above the diamond
      let abbrevLabel = this.towerLabels.get(tower.instanceId)
      if (abbrevLabel === undefined) {
        abbrevLabel = this.scene.add
          .text(sx, sy - hh - 2, abbrev(tower.id), {
            fontSize: '8px',
            color: '#ffffff',
            fontFamily: 'monospace',
            stroke: '#000000',
            strokeThickness: 2,
          })
          .setOrigin(0.5, 1.0)
          .setAlpha(0.85)
          .setDepth(7)
        this.towerLabels.set(tower.instanceId, abbrevLabel)
      }

      // Skip full redraw when tower state is unchanged since last frame.
      // Towers don't move, so the only state that changes is level and path choice.
      const cached = this.towerRenderStates.get(tower.instanceId)
      if (cached !== undefined && cached.level === tower.level && cached.path === tower.path) {
        continue
      }

      const { gfx, label } = obj

      gfx.clear()

      // ── Determine fill color and alpha by tower category/type ──
      let fillColor = ELEMENT_COLORS[tower.el] ?? 0x888888
      let fillAlpha = 1.0
      let outlineColor = ELEMENT_DARK[tower.el] ?? 0x444444
      let scaleH = 1.0

      if (tower.cat === 'wall') {
        fillColor = 0x333344
        outlineColor = 0x111122
        fillAlpha = 1.0
      } else if (tower.cat === 'terrain') {
        fillAlpha = 0.65
        scaleH = 0.75
      }

      // ── Goldmine indicator: gold tint ──
      const isGoldmine = tower.id === 'goldmine' || (tower.income !== undefined && tower.income > 0 && tower.cat !== 'attack')
      if (isGoldmine) {
        fillColor = 0xFFD700
        outlineColor = 0xB8860B
      }

      const drawHH = hh * scaleH

      // Shadow beneath tower (subtle, offset down)
      gfx.fillStyle(0x000000, 0.25)
      gfx.beginPath()
      gfx.moveTo(sx,        sy - drawHH + 4)
      gfx.lineTo(sx + hw,   sy + 4)
      gfx.lineTo(sx,        sy + drawHH + 4)
      gfx.lineTo(sx - hw,   sy + 4)
      gfx.closePath()
      gfx.fillPath()

      // ── Path tint overlay (path A = blue, path B = orange) ──
      if (tower.path === 'A') {
        gfx.fillStyle(0x4488FF, 0.18)
        gfx.beginPath()
        gfx.moveTo(sx,        sy - drawHH)
        gfx.lineTo(sx + hw,   sy)
        gfx.lineTo(sx,        sy + drawHH)
        gfx.lineTo(sx - hw,   sy)
        gfx.closePath()
        gfx.fillPath()
      } else if (tower.path === 'B') {
        gfx.fillStyle(0xFF8833, 0.18)
        gfx.beginPath()
        gfx.moveTo(sx,        sy - drawHH)
        gfx.lineTo(sx + hw,   sy)
        gfx.lineTo(sx,        sy + drawHH)
        gfx.lineTo(sx - hw,   sy)
        gfx.closePath()
        gfx.fillPath()
      }

      // ── Main diamond fill ──
      gfx.fillStyle(fillColor, fillAlpha)
      gfx.beginPath()
      gfx.moveTo(sx,        sy - drawHH)
      gfx.lineTo(sx + hw,   sy)
      gfx.lineTo(sx,        sy + drawHH)
      gfx.lineTo(sx - hw,   sy)
      gfx.closePath()
      gfx.fillPath()

      // ── Diamond outline ──
      gfx.lineStyle(1.5, outlineColor, 1.0)
      gfx.beginPath()
      gfx.moveTo(sx,        sy - drawHH)
      gfx.lineTo(sx + hw,   sy)
      gfx.lineTo(sx,        sy + drawHH)
      gfx.lineTo(sx - hw,   sy)
      gfx.closePath()
      gfx.strokePath()

      // ── Inner highlight (top-left facet) ──
      if (tower.cat !== 'wall') {
        gfx.lineStyle(1, 0xFFFFFF, 0.35)
        gfx.beginPath()
        gfx.moveTo(sx,        sy - drawHH)
        gfx.lineTo(sx - hw,   sy)
        gfx.strokePath()
        gfx.beginPath()
        gfx.moveTo(sx,        sy - drawHH)
        gfx.lineTo(sx + hw,   sy)
        gfx.strokePath()
      }

      // ── Level dots above the tower ──
      const dotCount = Math.max(1, Math.min(tower.level, 5))
      const dotRadius = Math.max(1.5, cellH * 0.065)
      const dotSpacing = dotRadius * 2.6
      const dotStartX = sx - ((dotCount - 1) * dotSpacing) / 2
      const dotY = sy - drawHH - dotRadius - 3

      for (let d = 0; d < dotCount; d++) {
        const dx = dotStartX + d * dotSpacing
        gfx.fillStyle(0xFFFFFF, 0.9)
        gfx.fillCircle(dx, dotY, dotRadius)
        gfx.lineStyle(0.5, outlineColor, 1.0)
        gfx.strokeCircle(dx, dotY, dotRadius)
      }

      // ── Path badge (A/B) — small colored circle top-right ──
      if (tower.path !== null) {
        const badgeX = sx + hw * 0.55
        const badgeY = sy - drawHH * 0.55
        const badgeR = Math.max(4, cellH * 0.18)
        const badgeColor = tower.path === 'A' ? 0x4488FF : 0xFF8833
        gfx.fillStyle(badgeColor, 1.0)
        gfx.fillCircle(badgeX, badgeY, badgeR)
        gfx.lineStyle(1, 0xFFFFFF, 0.9)
        gfx.strokeCircle(badgeX, badgeY, badgeR)
      }

      // ── Center label: $ for goldmine, ▪ for wall, element initial otherwise ──
      if (isGoldmine) {
        label.setPosition(sx, sy)
        label.setText('$')
        label.setStyle({ fontSize: `${Math.max(8, cellH * 0.38)}px`, color: '#663300' })
      } else if (tower.cat === 'wall') {
        label.setPosition(sx, sy)
        label.setText('▪')
        label.setStyle({ fontSize: `${Math.max(7, cellH * 0.28)}px`, color: '#8888AA' })
      } else {
        const letter = tower.el.charAt(0).toUpperCase()
        label.setPosition(sx, sy)
        label.setText(letter)
        label.setStyle({ fontSize: `${Math.max(7, cellH * 0.3)}px`, color: '#FFFFFF' })
      }

      // ── Update abbreviation label position (tower may have moved due to camera) ──
      abbrevLabel.setPosition(sx, sy - drawHH - dotRadius * 2 - 5)
      abbrevLabel.setText(abbrev(tower.id))

      // Save render state so we can skip redraws on unchanged frames.
      this.towerRenderStates.set(tower.instanceId, { level: tower.level, path: tower.path })
    }
  }

  /** Switch the rendered player's grid at runtime (spy mode). */
  setPlayerId(id: string): void {
    if (this.playerId === id) return
    this.playerId = id
    // Destroy every graphic object from the previous player's grid so the next
    // update() starts with empty maps and only renders the new player's towers.
    this._clearAll()
  }

  private _clearAll(): void {
    for (const [, obj] of this.towerObjects) {
      obj.gfx.destroy()
      obj.label.destroy()
    }
    this.towerObjects.clear()
    for (const [, lbl] of this.towerLabels) lbl.destroy()
    this.towerLabels.clear()
    for (const [, sprite] of this.towerSprites) sprite.destroy()
    this.towerSprites.clear()
    this.towerSpriteFrames.clear()
    this.towerRenderStates.clear()
    this._lastOriginKey = ''
  }

  /**
   * Render a tower using its per-entity atlas.
   *
   * Frame selection follows the binding naming convention:
   *   Level 1-2:       archer_l1_idle_0
   *   Level 3-5 path:  archer_l3a_idle_0 / archer_l3b_idle_0
   *
   * The animation frame index cycles at 4 fps using wall-clock time so all
   * towers of the same type stay in sync visually.
   *
   * Falls back to the procedural diamond if the atlas has no recognised frame.
   */
  private _renderTowerSprite(
    tower: PlacedTower,
    sx: number,
    sy: number,
    hh: number,
    cellH: number,
  ): void {
    const atlasKey = TEXTURE_KEYS.TOWER_ATLAS(tower.id)
    const frameIndex = AssetRegistry.animFrame(4, 4) // 4 frames @ 4 fps
    const frameKey = AssetRegistry.getTowerFrameKeyWithFallback(this.scene, tower, 'idle', frameIndex)

    let sprite = this.towerSprites.get(tower.instanceId)
    if (sprite === undefined) {
      // Create sprite — if frameKey is null the atlas exists but has no frames yet;
      // we still create a placeholder sprite (invisible) so we have an object to update.
      // Origin at (0.5, 0.63): the tower base in Blender renders sits at ~63% from the
      // top of the frame (derived from camera elevation arctan(0.5) = 26.565°).
      // Positioning at (sx, sy) aligns the base with the isometric cell center.
      sprite = this.scene.add.sprite(sx, sy, atlasKey, frameKey ?? undefined)
      sprite.setOrigin(0.5, 0.63)
      sprite.setDepth(5 + tower.gy * 0.01)
      sprite.setScale((cellH / 26) * 0.75)
      sprite.setVisible(frameKey !== null)
      this.towerSprites.set(tower.instanceId, sprite)
      this.towerSpriteFrames.set(tower.instanceId, frameKey ?? '')
    } else {
      sprite.setPosition(sx, sy)
      sprite.setDepth(5 + tower.gy * 0.01)
      sprite.setScale((cellH / 26) * 0.75)

      if (frameKey !== null) {
        // Only call setFrame when the frame actually changed (level/path/animation step)
        const lastFrame = this.towerSpriteFrames.get(tower.instanceId)
        if (lastFrame !== frameKey) {
          sprite.setFrame(frameKey)
          this.towerSpriteFrames.set(tower.instanceId, frameKey)
        }
        sprite.setVisible(true)
      } else {
        // Atlas loaded but no frame available → hide sprite, let procedural render
        sprite.setVisible(false)
      }
    }

    // ── Level / path label above sprite ──
    const levelText = tower.path !== null ? `Lv${tower.level}${tower.path}` : `Lv${tower.level}`
    let lvlLabel = this.towerLabels.get(tower.instanceId)
    if (lvlLabel === undefined) {
      lvlLabel = this.scene.add
        .text(sx, sy - hh - 14, levelText, {
          fontSize: '8px',
          color: '#ffffff',
          fontFamily: 'monospace',
          stroke: '#000000',
          strokeThickness: 2,
        })
        .setOrigin(0.5, 1.0)
        .setAlpha(0.85)
        .setDepth(7)
      this.towerLabels.set(tower.instanceId, lvlLabel)
    } else {
      lvlLabel.setPosition(sx, sy - hh - 14)
      lvlLabel.setText(levelText)
      lvlLabel.setVisible(true)
    }

    // ── Path badge (A/B) drawn on top of sprite when path is chosen ──
    // We overlay a small colored dot so the path is always visible regardless of art.
    if (tower.path !== null) {
      // Reuse the procedural gfx object for the badge
      let obj = this.towerObjects.get(tower.instanceId)
      if (obj === undefined) {
        const gfx = this.scene.add.graphics()
        gfx.setDepth(7)
        const label = this.scene.add.text(0, 0, '', { fontSize: '1px' }).setVisible(false)
        obj = { gfx, label }
        this.towerObjects.set(tower.instanceId, obj)
      }
      const { gfx } = obj
      gfx.clear()
      const hw = cellH * 0.44 * 0.44 // proportional to cellH
      const badgeX = sx + hw * 0.55
      const badgeY = sy - hh * 0.55
      const badgeR = Math.max(4, cellH * 0.18)
      const badgeColor = tower.path === 'A' ? 0x4488FF : 0xFF8833
      gfx.fillStyle(badgeColor, 1.0)
      gfx.fillCircle(badgeX, badgeY, badgeR)
      gfx.lineStyle(1, 0xFFFFFF, 0.9)
      gfx.strokeCircle(badgeX, badgeY, badgeR)
    }
  }

  private _destroyTowerObject(instanceId: string): void {
    const obj = this.towerObjects.get(instanceId)
    if (obj !== undefined) {
      obj.gfx.destroy()
      obj.label.destroy()
      this.towerObjects.delete(instanceId)
    }
    const abbrevLabel = this.towerLabels.get(instanceId)
    if (abbrevLabel !== undefined) {
      abbrevLabel.destroy()
      this.towerLabels.delete(instanceId)
    }
    const sprite = this.towerSprites.get(instanceId)
    if (sprite !== undefined) {
      sprite.destroy()
      this.towerSprites.delete(instanceId)
    }
    this.towerSpriteFrames.delete(instanceId)
    this.towerRenderStates.delete(instanceId)
  }

  destroy(): void {
    this._clearAll()
  }
}
